import { useSoc, type SocOrg } from '@/api/soc';
import { Chip, CompanyCell, Empty, ListPage, PageLoading, Th, tableWrap, theadCls, useListState, type FilterDef } from '@/components/network/NetworkKit';
import { HuntressLink } from '@/components/soc/SocKit';
import { fmtRelative } from '@/lib/cobalt-helpers';

/**
 * Huntress organisations (= clients) and the Autotask company each resolves to: its Microsoft 365
 * tenant through the CIPP mapping, else the same name in Autotask. Overrides: Admin → Client mappings.
 */

const SOURCE_LABEL: Record<SocOrg['company']['source'], string> = { tenant: 'via M365 tenant (CIPP)', name: 'same name in Autotask', manual: 'set by an admin', ignored: 'ignored', none: 'not mapped' };
const FILTERS: FilterDef<SocOrg>[] = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'unmapped', label: 'Not mapped', test: (o) => o.company.companyId == null && o.company.source !== 'ignored' },
  { id: 'siem', label: 'SIEM log sources', test: (o) => o.logSources > 0 },
];

export default function SocOrganizationsPage() {
  const { data, isLoading, error, isFetching } = useSoc({ scoped: true });
  const items = data?.configured ? data.orgs : [];
  const l = useListState('cosmos:soc:orgs:show', FILTERS, items, (o) => `${o.name} ${o.company.companyName ?? ''} ${o.key}`);

  return (
    <ListPage area="SOC" title="Organisations" count={data?.configured ? l.rows.length : null} isFetching={isFetching}
      subtitle={data?.configured && <>Huntress organisations ↔ Autotask companies · read {fmtRelative(data.fetchedAt)}</>}
      search={{ q: l.q, setQ: l.setQ, placeholder: 'Organisation or client' }}
      filters={{ defs: FILTERS, show: l.show, pick: l.pick, counts: l.counts }}>
      <PageLoading isLoading={isLoading} error={error} configured={data?.configured} vendor="Huntress" />
      {data?.configured && (
        <div className={tableWrap}>
          <table className="w-full text-sm">
            <thead className={theadCls}>
              <tr><Th>Organisation</Th><Th>Autotask company</Th><Th right>Agents</Th><Th right>ITDR identities</Th><Th right>SIEM sources</Th><Th right>Incidents</Th><Th /></tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {l.rows.length === 0 && <Empty cols={7} />}
              {l.rows.map((o) => (
                <tr key={o.id} className="hover:bg-muted/30">
                  <td className="px-3 py-2"><div className="font-medium text-foreground">{o.name}</div><div className="text-[11px] text-muted-foreground">{o.key}</div></td>
                  <td className="max-w-[280px] px-3 py-2 text-xs">
                    <CompanyCell companyId={o.company.companyId} companyName={o.company.companyName} fallback={o.name} />
                    <div className="mt-0.5">{o.company.companyId == null && o.company.source !== 'ignored' ? <Chip tone="warn">not mapped</Chip> : <span className="text-[11px] text-muted-foreground">{SOURCE_LABEL[o.company.source]}</span>}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{o.agents}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{o.billableIdentities}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{o.logSources}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{o.incidents}</td>
                  <td className="px-3 py-2 text-right"><HuntressLink href={o.portalUrl} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ListPage>
  );
}
